import { useEffect, useRef, useState } from "react";
import { KeyRound, Loader2, MessageSquare, MoreHorizontal, Plug, RefreshCw, Trash2 } from "lucide-react";
import { ConnectionLogo } from "./ConnectionLogo";
import { cardFacts, cardMeta, cardSubline, isWorking, jobLine } from "./connectionsModel";

// One card per connection, the C2 anatomy: a short band in the status's colour with the logo, the status word and
// a dots menu; the body with the name, the account, the reason, the last check and exactly one action where it
// matters. Test and Remove live behind the dots unless they are that one action.

/** Higgsfield reports itself as a key card, which cannot reconnect through the key path; until it is connected it signs in again. */
export function canReconnectCard(card) {
  return card.app === "higgsfield" ? card.status !== "connected" : Boolean(card.canReconnect);
}

export function removeWord(card) {
  return card.kind === "api_key" ? "Remove" : "Disconnect";
}

export function canReplaceKey(card) {
  return card.kind === "api_key" && card.app !== "higgsfield";
}

const TONE_WORDS = { good: "Connected", bad: "Not working", quiet: "Saved" };

/** The sentence the agent gets when a person asks it to fix a connection that is not working or did not install. */
export function fixMessage(label, { reason = "", failed = false } = {}) {
  const said = reason ? ` The dashboard says: "${String(reason).trim().replace(/"/g, "'")}".` : "";
  return failed
    ? `${label} did not install on your machine.${said} Can you check what went wrong and fix it, or tell me what you need from me?`
    : `${label} is not working.${said} Can you check what is wrong and fix it, or tell me what you need from me?`;
}

/**
 * What the card leads with, and what goes behind the dots.
 * primary: "fix" | "reconnect" | "replace" | "test" | "working" | null; menu: a subset of ["test", "reconnect", "replace", "fix", "remove"].
 * `canFix` = the chat bubble is available. Any card that is not working then leads with Fix with <agent>; Sign in
 * again and Replace key wait behind the dots. Without the bubble, the direct fix leads.
 */
export function cardActions(card, canManage, canFix = false) {
  const working = isWorking(card.job);
  const reconnect = canManage && canReconnectCard(card);
  const replace = canManage && canReplaceKey(card);
  const remove = canManage && Boolean(card.canRemove);
  let primary = null;
  if (working) primary = "working";
  else if (card.status === "not_working" && canFix) primary = "fix";
  else if (card.status === "not_working" && reconnect) primary = "reconnect";
  else if (card.status === "not_working" && replace) primary = "replace";
  else if (card.status === "connected" && card.canTest) primary = "test";
  else if (card.status === "not_verified" && replace) primary = "replace";
  else if (card.canTest) primary = "test";
  const menu = [];
  if (card.canTest && primary !== "test" && !working) menu.push("test");
  if (reconnect && primary !== "reconnect") menu.push("reconnect");
  if (replace && primary !== "replace") menu.push("replace");
  if (canFix && card.status === "not_working" && primary !== "fix" && !working) menu.push("fix");
  if (remove) menu.push("remove");
  return { primary, menu };
}

function CardMenu({ card, items, agentName, onTest, onReconnect, onReplace, onFix, onRemove, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", escape); };
  }, [open]);
  if (!items.length) return null;
  const pick = (action) => { setOpen(false); action(card); };
  return (
    <span className="cx-menu" ref={ref}>
      <button type="button" className="cx-card__more" data-testid={`agent-connection-${card.app}-more`} aria-haspopup="menu" aria-expanded={open} aria-label={`More for ${card.label}`} disabled={disabled} onClick={() => setOpen((value) => !value)}>
        <MoreHorizontal size={15} />
      </button>
      {open ? (
        <div className="cx-menu__list" role="menu">
          {items.includes("test") ? <button type="button" role="menuitem" className="cx-menu__item" data-testid={`agent-connection-${card.app}-test`} onClick={() => pick(onTest)}><RefreshCw size={13} />Test</button> : null}
          {items.includes("reconnect") ? <button type="button" role="menuitem" className="cx-menu__item" data-testid={`agent-connection-${card.app}-reconnect`} onClick={() => pick(onReconnect)}><Plug size={13} />Sign in again</button> : null}
          {items.includes("replace") ? <button type="button" role="menuitem" className="cx-menu__item" data-testid={`agent-connection-${card.app}-replace`} onClick={() => pick(onReplace)}><KeyRound size={13} />Replace key</button> : null}
          {items.includes("fix") ? <button type="button" role="menuitem" className="cx-menu__item" data-testid={`agent-connection-${card.app}-fix`} onClick={() => pick(onFix)}><MessageSquare size={13} />Fix with {agentName}</button> : null}
          {items.includes("remove") ? <button type="button" role="menuitem" className="cx-menu__item cx-menu__item--danger" data-testid={`agent-connection-${card.app}-remove`} onClick={() => pick(onRemove)}><Trash2 size={13} />{removeWord(card)}</button> : null}
        </div>
      ) : null}
    </span>
  );
}

export function ConnectionCard({ card, canManage = false, busy = false, agentName = "your agent", canFix = false, onTest, onRemove, onReconnect, onReplace, onFix, now }) {
  const working = busy || isWorking(card.job);
  const meta = cardMeta(card);
  const tone = working ? "busy" : meta.tone;
  const word = working ? (jobLine(card.job)?.replace(/…$/, "") || "Working") : (TONE_WORDS[meta.tone] || meta.label);
  const bad = card.status === "not_working" && !working;
  const job = jobLine(card.job);
  const facts = cardFacts(card, now);
  const { primary, menu } = cardActions({ ...card, job: busy ? { status: "applying", action: "test" } : card.job }, canManage, canFix && Boolean(onFix));
  const handlers = { test: onTest, replace: onReplace, remove: onRemove };
  return (
    <article className={`cx-card cx-card--${tone}`} data-testid={`agent-connection-${card.app}-card`} data-kind={card.kind} data-status={card.status}>
      <div className={`cx-card__band cx-card__band--${tone}`}>
        <ConnectionLogo app={card.app} label={card.label} size={22} />
        <span className="cx-card__state" data-testid={`agent-connection-${card.app}-status`}>{working ? <Loader2 size={11} className="cx-spin" aria-hidden="true" /> : null}{word}</span>
        <CardMenu card={card} items={menu} agentName={agentName} disabled={working} onTest={handlers.test} onReconnect={onReconnect} onReplace={handlers.replace} onFix={onFix} onRemove={handlers.remove} />
      </div>
      <div className="cx-card__body">
        <h3 className="cx-card__name">{card.label}</h3>
        <p className="cx-card__sub" data-testid={card.accountLabel ? `agent-connection-${card.app}-account` : undefined}>{cardSubline(card)}</p>
        {job && card.job?.status === "failed" ? <p className="cx-card__reason" data-testid={`agent-connection-${card.app}-reason`}>{job}</p>
          : job ? <p className="cx-card__job">{job}</p>
          : bad && card.reason ? <p className="cx-card__reason" data-testid={`agent-connection-${card.app}-reason`}>{card.reason}</p>
          : null}
        <div className="cx-card__foot">
          <div className="cx-card__facts">{facts.map((fact) => <span key={fact}>{fact}</span>)}</div>
          {primary === "working" ? <span className="cx-btn cx-btn--sm cx-btn--ghost cx-btn--quiet" aria-disabled="true"><Loader2 size={13} className="cx-spin" />{word}…</span> : null}
          {primary === "reconnect" ? <button type="button" className="cx-btn cx-btn--sm cx-btn--primary cx-card__action" data-testid={`agent-connection-${card.app}-reconnect`} onClick={() => onReconnect?.(card)}><Plug size={13} />Sign in again</button> : null}
          {primary === "replace" ? <button type="button" className={`cx-btn cx-btn--sm ${bad ? "cx-btn--primary cx-card__action" : "cx-btn--ghost"}`} data-testid={`agent-connection-${card.app}-replace`} onClick={() => onReplace?.(card)}><KeyRound size={13} />Replace key</button> : null}
          {primary === "test" ? <button type="button" className="cx-btn cx-btn--sm cx-btn--ghost" data-testid={`agent-connection-${card.app}-test`} onClick={() => onTest?.(card)}><RefreshCw size={13} />Test</button> : null}
          {primary === "fix" ? <button type="button" className="cx-btn cx-btn--sm cx-btn--primary cx-card__action" data-testid={`agent-connection-${card.app}-fix`} onClick={() => onFix?.(card)}><MessageSquare size={13} />Fix with {agentName}</button> : null}
        </div>
      </div>
    </article>
  );
}

/** An Add that has a job running but no card yet. A failed install can be handed to the agent. */
export function PendingCard({ entry, guide, agentName = "your agent", onFix }) {
  const failed = entry.job?.status === "failed";
  const detail = failed
    ? entry.job.error || "The agent could not take it. Try again."
    : (entry.job?.action === "handoff" && guide?.submitted) || "Being added to your agent. About a minute.";
  const word = failed ? "Failed" : (jobLine(entry.job)?.replace(/…$/, "") || "Working");
  return (
    <article className={`cx-card cx-card--${failed ? "bad" : "busy"} cx-card--pending`} data-testid={`agent-connection-${entry.app}-pending`}>
      <div className={`cx-card__band cx-card__band--${failed ? "bad" : "busy"}`}>
        <ConnectionLogo app={entry.app} label={entry.label} size={22} />
        <span className="cx-card__state">{failed ? null : <Loader2 size={11} className="cx-spin" aria-hidden="true" />}{word}</span>
      </div>
      <div className="cx-card__body">
        <h3 className="cx-card__name">{entry.label}</h3>
        <p className="cx-card__sub">{failed ? "Did not install" : "On its way"}</p>
        <p className={failed ? "cx-card__reason" : "cx-card__job"}>{detail}</p>
        {failed && onFix ? (
          <div className="cx-card__foot">
            <div className="cx-card__facts"><span>Did not install</span></div>
            <button type="button" className="cx-btn cx-btn--sm cx-btn--primary cx-card__action" data-testid={`agent-connection-${entry.app}-fix`} onClick={() => onFix(entry)}><MessageSquare size={13} />Fix with {agentName}</button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

export default ConnectionCard;
